import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../utils/api';

// Slide-up transition so the modal behaves like a bottom sheet on mobile.
const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialEmail?: string;
}

const purpleButtonSx = {
  py: 1.5,
  backgroundColor: '#533B7D',
  '&:hover': { backgroundColor: '#4a337a' },
  borderRadius: 2,
  textTransform: 'none' as const,
  fontSize: '16px',
  fontWeight: 'bold' as const,
};

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
  initialEmail = '',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { login } = useAuth();

  const [step, setStep] = useState<'request' | 'reset'>('request');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // Reset internal state whenever the modal is (re)opened.
  useEffect(() => {
    if (open) {
      setStep('request');
      setEmail(initialEmail);
      setCode('');
      setNewPassword('');
      setShowPassword(false);
      setError('');
      setInfo('');
      setLoading(false);
    }
  }, [open, initialEmail]);

  const parseError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d.msg || d).join(', ');
    return fallback;
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setStep('reset');
      setInfo(`If an account exists for ${email.trim()}, we've sent a 6-digit code. It expires in 10 minutes.`);
    } catch (err: any) {
      setError(parseError(err, 'Could not send a reset code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.resetPassword(email.trim(), code.trim(), newPassword);
      // Reset succeeded — log the user straight in with the new password.
      await login({ email: email.trim(), password: newPassword });
      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(parseError(err, 'Invalid or expired code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? SlideUpTransition : undefined}
      sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      PaperProps={{
        sx: {
          p: 2,
          overscrollBehavior: 'contain',
          ...(isMobile
            ? { m: 0, width: '100%', maxWidth: '100%', borderRadius: '20px 20px 0 0', pb: 'env(safe-area-inset-bottom)' }
            : { borderRadius: 3 }),
        },
      }}
    >
      <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 2 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Reset password
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

        {step === 'request' ? (
          <Box component="form" onSubmit={handleRequestCode} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your account email and we'll send you a code to reset your password.
            </Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              id="reset-email"
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button type="submit" fullWidth variant="contained" disabled={loading || !email.trim()} sx={{ mt: 1, mb: 1, ...purpleButtonSx }}>
              {loading ? 'Sending...' : 'Send reset code'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="reset-code"
              label="6-digit code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '6px', fontWeight: 700 } }}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="reset-new-password"
              label="New password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="At least 6 characters"
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || code.length !== 6 || newPassword.length < 6}
              sx={{ mt: 2, mb: 1, ...purpleButtonSx }}
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => { setStep('request'); setError(''); setInfo(''); }}
                sx={{ color: '#533B7D' }}
              >
                Use a different email or resend code
              </Link>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
